import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ProspectsContext, type IProspectsContextValue } from '../../hooks/useProspect/useProspect';
import { useUser } from '../../hooks/useUser/useUser';
import type {
  ICreateProspectContact,
  ICreateProspectInput,
  IProspect,
  IUpdateProspectContact,
  IUpdateProspectInput
} from '../../interfaces/prospects';
import ProspectsService from '../../services/api/prospects/prospects';
import { ProspectStatus } from '../../types/prospects';
import { buildProspectActivationGroup } from '../../utils/checks/activationChecks';

const SEARCH_DEBOUNCE_MS = 300;

interface IProspectsProviderProps {
  children: React.ReactNode;
}

const ProspectsProvider = ({ children }: IProspectsProviderProps) => {
  const { brokerId, loading: userLoading } = useUser();

  const [prospects, setProspects] = useState<IProspect[]>([]);
  const [hasAnyProspect, setHasAnyProspect] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [deferredQuery, setDeferredQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ProspectStatus | null>(ProspectStatus.PendingQuote);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [trackedBrokerId, setTrackedBrokerId] = useState(brokerId);
  const [newProspectCreatedId, setNewProspectCreatedId] = useState<number | null>(null);

  const fetchIdRef = useRef(0);
  const pendingSelectIdRef = useRef<number | null>(null);

  if (brokerId !== trackedBrokerId) {
    setTrackedBrokerId(brokerId);
    setStatusFilter(ProspectStatus.PendingQuote);
    setQuery('');
    setSelectedId(null);
    setHasAnyProspect(null);
    setLoading(true);
  }

  useEffect(() => {
    if (!query) {
      setDeferredQuery('');
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    const timer = setTimeout(() => {
      setDeferredQuery(query);
      setIsSearching(false);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const fetchProspects = useCallback(
    async (filterStatus: ProspectStatus | null) => {
      if (userLoading) return;
      if (!brokerId) {
        setProspects([]);
        setHasAnyProspect(false);
        setLoading(false);
        return;
      }
      const id = ++fetchIdRef.current;
      try {
        setLoading(true);
        const [data, baseData] = await Promise.all([
          ProspectsService.getProspects(brokerId, { status: filterStatus || undefined }).catch(() => [] as IProspect[]),
          ProspectsService.getProspects(brokerId, { pageSize: 1 }).catch(() => [] as IProspect[])
        ]);
        if (id !== fetchIdRef.current) return;
        setProspects(data);
        setHasAnyProspect(data.length > 0 || baseData.length > 0);

        if (pendingSelectIdRef.current != null) {
          const exists = data.some((p) => p.id === pendingSelectIdRef.current);
          setSelectedId(exists ? pendingSelectIdRef.current : (data[0]?.id ?? null));
          pendingSelectIdRef.current = null;
        }
      } catch {
        if (id === fetchIdRef.current) {
          setProspects([]);
          setHasAnyProspect(false);
        }
      } finally {
        if (id === fetchIdRef.current) {
          setLoading(false);
        }
      }
    },
    [brokerId, userLoading]
  );

  useEffect(() => {
    void fetchProspects(statusFilter);
  }, [fetchProspects, statusFilter, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((k) => k + 1), []);

  const requestSelectAfterRefresh = useCallback((id: number) => {
    pendingSelectIdRef.current = id;
  }, []);

  const createProspect = useCallback<IProspectsContextValue['createProspect']>(
    async (body: ICreateProspectInput) => {
      if (!brokerId) {
        throw new Error('Cannot create a prospect without a broker');
      }
      const response = await ProspectsService.createProspect(brokerId, body);
      pendingSelectIdRef.current = response.id;
      setStatusFilter(ProspectStatus.PendingQuote);
      setNewProspectCreatedId(response.id);
      setRefreshTrigger((k) => k + 1);
      return response;
    },
    [brokerId]
  );

  const updateProspect = useCallback(
    async (id: number, input: IUpdateProspectInput) => {
      if (!brokerId) return;
      await ProspectsService.updateProspect(brokerId, id, input);
      refresh();
    },
    [brokerId, refresh]
  );

  const createContact = useCallback(
    async (id: number, input: ICreateProspectContact) => {
      if (!brokerId) {
        throw new Error('Cannot create a contact without a broker');
      }
      const response = await ProspectsService.createProspectContact(brokerId, id, input);
      refresh();
      return response;
    },
    [brokerId, refresh]
  );

  const updateContact = useCallback(
    async (id: number, contactId: number, input: IUpdateProspectContact) => {
      if (!brokerId) return;
      await ProspectsService.updateProspectContact(brokerId, id, contactId, input);
      refresh();
    },
    [brokerId, refresh]
  );

  const filtered = useMemo(() => {
    const lower = deferredQuery.toLowerCase().trim();
    return prospects.filter((p) => !p.error && (lower ? p.companyName.toLowerCase().includes(lower) : true));
  }, [prospects, deferredQuery]);

  const selected = useMemo(() => {
    if (selectedId !== null) {
      const found = prospects.find((p) => p.id === selectedId);
      if (found) return found;
    }
    return filtered[0] ?? null;
  }, [filtered, selectedId, prospects]);

  const selectedActivationGroup = useMemo(() => (selected ? buildProspectActivationGroup(selected) : null), [selected]);

  const getProspectById = useCallback((id: number) => prospects.find((p) => p.id === id), [prospects]);

  const isLoading = loading || userLoading;

  const value = useMemo<IProspectsContextValue>(
    () => ({
      prospects,
      filtered,
      loading: isLoading,
      hasAnyProspect,
      statusFilter,
      setStatusFilter,
      query,
      setQuery,
      isSearching,
      selectedId,
      setSelectedId,
      selected,
      selectedActivationGroup,
      getProspectById,
      refresh,
      createProspect,
      updateProspect,
      createContact,
      updateContact,
      requestSelectAfterRefresh,
      newProspectCreatedId,
      setNewProspectCreatedId
    }),
    [
      prospects,
      filtered,
      isLoading,
      hasAnyProspect,
      statusFilter,
      query,
      isSearching,
      selectedId,
      selected,
      selectedActivationGroup,
      getProspectById,
      refresh,
      createProspect,
      updateProspect,
      createContact,
      updateContact,
      requestSelectAfterRefresh,
      setNewProspectCreatedId,
      newProspectCreatedId
    ]
  );

  return <ProspectsContext.Provider value={value}>{children}</ProspectsContext.Provider>;
};

export default ProspectsProvider;
